/**
 * lazy-accordion.js
 * Defers rendering of heavy accordion bodies until the accordion is first opened.
 *
 * Usage in Liquid:
 *   <details ... data-lazy-details>
 *     <summary>...</summary>
 *     <div class="accordion-details__content">
 *       <template data-lazy-content>
 *         ...heavy markup (images, sliders, many rows)...
 *       </template>
 *     </div>
 *   </details>
 *
 * On the first open (click / keyboard / programmatic), the template's content is
 * cloned into place before the template, and the template is removed. Custom
 * elements (sliders, modals) upgrade automatically on insertion; delegated
 * listeners (tooltips) keep working. After hydration a `lazyaccordion:hydrated`
 * event is dispatched on the <details> so widgets can (re)sync themselves.
 *
 * Notes:
 * - Uses a document-level capture click listener so hydration runs BEFORE the
 *   accordion measures scrollHeight for its open animation -> no clipped height.
 * - Delegation means dynamically inserted blocks (e.g. after a variant change
 *   re-renders the product section) work without re-initialising.
 */
(function () {
  'use strict';

  if (window.__lazyAccordionInit) return;
  window.__lazyAccordionInit = true;

  var HYDRATED = '__lazyAccordionHydrated';

  function hydrate(details) {
    if (!details || details[HYDRATED]) return;
    details[HYDRATED] = true;

    var tpl = details.querySelector('template[data-lazy-content]');
    if (tpl && tpl.content) {
      tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl);
      tpl.parentNode.removeChild(tpl);
    }

    // Let widgets inside the freshly inserted content (re)initialise / re-sync.
    try {
      details.dispatchEvent(
        new CustomEvent('lazyaccordion:hydrated', { bubbles: true })
      );
    } catch (e) {
      /* no-op */
    }
  }

  // Hydrate on first interaction. Capture phase = runs before the accordion's
  // own summary click handler, so the open animation sees the real height.
  document.addEventListener(
    'click',
    function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      var summary = target.closest('summary');
      if (!summary) return;
      var details = summary.closest('details[data-lazy-details]');
      if (details) hydrate(details);
    },
    true
  );

  // Hydrate blocks that are already open, or everything in the theme editor so
  // merchants can see/edit the content.
  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var inDesignMode = !!(window.Shopify && window.Shopify.designMode);
    var nodes = scope.querySelectorAll('details[data-lazy-details]');
    for (var i = 0; i < nodes.length; i++) {
      var d = nodes[i];
      if (
        inDesignMode ||
        d.hasAttribute('open') ||
        d.getAttribute('aria-expanded') === 'true'
      ) {
        hydrate(d);
      }
    }
  }

  if (document.readyState !== 'loading') {
    scan();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  }

  // Shopify theme editor: re-scan sections as they are (re)loaded.
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
})();
